import styled, { keyframes } from 'styled-components';
import { Icon } from 'components/Icon';
import { row } from 'styles/globalStyles';
import { LineChart } from 'components/charts/LineChart';
import { useMemo, memo, useCallback } from 'react';
import * as dayjs from 'dayjs';

export function HourContainer({loading = false, data, error, onRetry}) {
  const forecasts = useMemo(() => {
    if (!data?.Fcasts?.Fcast?.threeHourFcasts?.threeHourFcast) {
      return [];
    }
    
    const forecastsData = [];
    const currentDay = dayjs();
    const threeHourFcasts = data.Fcasts.Fcast.threeHourFcasts.threeHourFcast;
    
    for (let i = 0; i < threeHourFcasts.length; i++) {
      const forecast = threeHourFcasts[i];
      const forecastDay = dayjs(`${forecast.aplYmd}${forecast.aplHour}0000`, 'YYYYMMDDHHmmss');

      if (forecastDay.isSame(currentDay) || forecastDay.isAfter(currentDay)) {
        forecastsData.push(forecast);
      }
    }
    
    return forecastsData;
  }, [data]);
  
  const chartData = useMemo(() => forecasts.map((forecast) => ({
    time: forecast.aplHour,
    temperature: forecast.tmpr,
  })), [forecasts]);
  
  const handleRetry = useCallback(() => {
    if (onRetry) onRetry();
  }, [onRetry]);

  return (
    <HourContainerStyle>
      <p className="title">시간별 예보</p>
      {loading ? (
        <HourBoxSkeleton>
          <div className="skeleton-type"></div>
          <div className="skeleton-hours">
            {Array(8).fill().map((_, index) => (
              <div key={`skeleton_${index}`} className="skeleton-hour-item">
                <div className="skeleton-line hour"></div>
                <div className="skeleton-circle"></div>
                <div className="skeleton-line chart"></div>
                <div className="skeleton-line"></div>
                <div className="skeleton-line"></div>
                <div className="skeleton-line"></div>
                <div className="skeleton-line"></div>
              </div>
            ))}
          </div>
        </HourBoxSkeleton>
      ) : ( error || !data) ? (
        <HourBoxError>
          <div className="error-container">
            <Icon name="Alert" $width={24} $height={24} />
            <p className="error-message">데이터를 불러오는 중 오류가 발생했습니다.</p>
            <button type="button" className="retry-button" onClick={handleRetry}>
              <Icon name="Refresh" $width={16} $height={16} />
              다시 시도
            </button>
          </div>
        </HourBoxError>
      ) : (
        <HourBox>
          <div className="type">
            <p>
              강수량<br/><span className="type_unit">mm</span>
            </p>
            <p>
              강수확률<br/><span className="type_unit">%</span>
            </p>
            <p>
              바람<br/><span className="type_unit">m/s</span>
            </p>
            <p>
              습도<br/><span className="type_unit">%</span>
            </p>
          </div>
          <HoursList>
            {forecasts.map((item, index) => (
              <ForecastItem 
                key={`hour_${item.aplYmd}_${item.aplHour}`} 
                item={item} 
                index={index} 
                chartData={chartData} 
              />
            ))}
          </HoursList>
        </HourBox>
      )}
    </HourContainerStyle>
  );
}

const ForecastItem = memo(({ item, index, chartData }) => (
  <li>
    <p className="hour">{item.aplHour}시</p>
    <div className="icon_box">
      <Icon name="Sun" $width={24} $height={24} />
    </div>
    <div className="chart">
      {index === 0 && (
        <LineChart list={chartData} />
      )}
    </div>
    <div className="box_h_24">
      <p className="h_24">{item.rainAmt}</p>
    </div>
    <div className="box_h_24">
      <p className="h_24">{item.prep === '' ? '-' : `${item.prep}%`}</p>
    </div>
    <div className="box_h_24">
      <WindBox>
        <Icon name="Wind" $width={16} $height={16} $transform={`rotate(${item.windDrctn}deg)`}/>
        <p>{item.windSpd}</p>
      </WindBox>
    </div>
    <div className="box_h_24">
      <p className="h_24">{item.humd}</p>
    </div>
  </li>
));

const shimmer = keyframes`
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
`;

const HourContainerStyle = styled.div`
  padding-top: 20px;

  p {
    color: #252729;
  }

  .title {
    font-size: 18px;
    font-weight: 800;
    line-height: 1.5;
  }

  .type {
    min-width: 80px;
    width: 80px;
    display: flex;
    flex-direction: column;
    padding-top: 133px;
    gap: 8px;
    font-size: 14px;
    line-height: 1.5;
    color: #46494b;
    font-weight: 500;
    padding-right: 8px;
    text-align: center;

    p {
      padding: 1.5px 0;
    }
  }

  .type .type_unit {
    font-size: 11px;
    line-height: 1.2;
    color: #c2c4c8;
  }
`;

const HourBox = styled.div`
  ${row}
  margin: 24px 0 16px;
`;

const HourBoxSkeleton = styled.div`
  ${row}
  margin: 24px 0 16px;
  width: 100%;

  .skeleton-type {
    min-width: 67px;
    width: 67px;
    padding-right: 8px;
    display: flex;
    flex-direction: column;
    padding-top: 133px;
    gap: 8px;
  }

  .skeleton-hours {
    ${row}
    border-left: 1px solid #eff2f5;
    overflow-x: hidden;
    flex-grow: 1;
  }

  .skeleton-hour-item {
    min-width: 44px;
    max-width: 44px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .skeleton-line {
    height: 16px;
    width: 30px;
    background: linear-gradient(to right, #f0f0f0 8%, #e0e0e0 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.5s infinite linear;
    border-radius: 4px;
  }

  .skeleton-line.hour {
    width: 25px;
    margin-bottom: 4px;
  }

  .skeleton-line.chart {
    width: 40px;
    height: 56px;
  }

  .skeleton-circle {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: linear-gradient(to right, #f0f0f0 8%, #e0e0e0 18%, #f0f0f0 33%);
    background-size: 800px 104px;
    animation: ${shimmer} 1.5s infinite linear;
  }
`;

const HourBoxError = styled.div`
  margin: 24px 0 16px;
  border: 1px solid #eff2f5;
  border-radius: 8px;
  padding: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  width: 100%;
  
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }
  
  .error-message {
    color: #e74c3c;
    font-size: 14px;
    line-height: 1.5;
    text-align: center;
  }
  
  .retry-button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: #e74c3c;
    border: none;
    border-radius: 4px;
    font-size: 14px;
    color: white;
    cursor: pointer;
    transition: background-color 0.3s;
    
    &:hover {
      background-color: #c0392b;
    }
  }
`;

const HoursList = styled.ul`
  ${row}
  border-left: 1px solid #eff2f5;
  overflow-x: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  .chart {
    width: 100%;
    height: 56px;
  }

  li {
    min-width: 44px;
    max-width: 44px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }

  .icon_box {
    height: 32px;
    padding: 4px 0;
  }
  .hour {
    color: #7a7d7f;
    font-size: 14px;
    line-height: 21px;
    font-weight: 500;
  }
  .box_h_24 {
    height: 45px;
    padding-top: 10px;
    padding-left: 10px;
  }
  .h_24 {
    line-height: 24px;
    font-size: 14px;
    line-height: 24px;
  }
`;

const WindBox = styled.div`
  ${row}
  align-items: center;
  color: #252729;
  font-size: 14px;
  line-height: 24px;
`;